import { useState, useCallback } from "react";
import { notification } from "antd";

import { Transaction, TransactionParameters, NotificationType } from "../types";
import { submitTransactionsToApi } from "../utils/api";
import { generateTransactionData } from "../utils/transactionGenerator";

/**
 * Custom hook for notification management
 */
const useNotification = () => {
  return useCallback((message: string, type: NotificationType) => {
    notification[type]({
      message: type.toUpperCase(),
      description: message,
      duration: 5,
    });
  }, []);
};

/**
 * Custom hook for managing transactions in the sandbox environment
 */
export const useTransactions = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const showNotification = useNotification();

  // Generate new transactions
  const generateTransactions = useCallback(
    (params: TransactionParameters) => {
      const newTransactions = generateTransactionData(params);

      // Add new transactions to the beginning of the existing transactions array
      setTransactions((prevTransactions) => [
        ...newTransactions,
        ...prevTransactions,
      ]);

      showNotification(
        `Successfully generated ${params.count} transactions`,
        "success"
      );

      return newTransactions;
    },
    [showNotification]
  );

  // Remove a single transaction
  const removeTransaction = useCallback(
    (id: number) => {
      setTransactions((prev) => prev.filter((tx) => tx.id !== id));
      showNotification("Transaction removed", "success");
    },
    [showNotification]
  );

  // Remove selected transactions
  const removeSelectedTransactions = useCallback(() => {
    if (selectedRowIds.length === 0) return;

    setTransactions((prev) =>
      prev.filter((tx) => !selectedRowIds.includes(tx.id))
    );

    showNotification(
      `Removed ${selectedRowIds.length} transaction(s)`,
      "success"
    );

    setSelectedRowIds([]);
  }, [selectedRowIds, showNotification]);

  // Clear all transactions
  const clearTransactions = useCallback(() => {
    setTransactions([]);
    setSelectedRowIds([]);
    showNotification("Cleared all generated transactions", "success");
  }, [showNotification]);

  // Submit transactions to the API
  const submitTransactions = useCallback(
    async (email: string) => {
      if (!email || transactions.length === 0) return;

      setLoading(true);
      showNotification("Processing...", "info");

      try {
        // Call the API util function to submit transactions
        await submitTransactionsToApi(email, transactions);

        showNotification(
          `Successfully submitted ${transactions.length} transactions to the sandbox environment for ${email}`,
          "success"
        );

        setSelectedRowIds([]);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to submit transactions. Please try again.";

        showNotification(errorMessage, "error");
      } finally {
        setLoading(false);
      }
    },
    [transactions, showNotification]
  );

  return {
    transactions,
    loading,
    selectedRowIds,
    setSelectedRowIds,
    generateTransactions,
    removeTransaction,
    removeSelectedTransactions,
    clearTransactions,
    submitTransactions,
    showNotification,
  };
};
